"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const isNil_1 = __importDefault(require("lodash/isNil"));
const classnames_1 = __importDefault(require("classnames"));
const bem_helper_1 = require("../../helpers/bem-helper");
const backwardCompatibilityForProperties_1 = require("../../helpers/backwardCompatibilityForProperties");
const colors_vars_map_1 = require("../../utils/colors-vars-map");
const AvatarConstants_1 = require("./AvatarConstants");
const AvatarBadge_1 = require("./AvatarBadge");
const AvatarContent_1 = require("./AvatarContent");
const Tooltip_1 = __importDefault(require("../Tooltip/Tooltip"));
const ClickableWrapper_1 = __importDefault(require("../Clickable/ClickableWrapper"));
const Dialog_1 = __importDefault(require("../Dialog/Dialog"));
require("./Avatar.scss");
const AVATAR_CSS_BASE_CLASS = "monday-style-avatar";
const bemHelper = (0, bem_helper_1.BEMClass)(AVATAR_CSS_BASE_CLASS);
const Avatar = ({ id, type = AvatarConstants_1.AvatarType.TEXT, className, textClassName = "", size = AvatarConstants_1.AvatarSize.LARGE, src, icon, text, tooltipProps, ariaLabel, withoutTooltip = false, role, backgroundColor = colors_vars_map_1.elementColorsNames.CHILI_BLUE, square, disabled, 
// Backward compatibility for props naming
isSquare, 
// Backward compatibility for props naming
isDisabled, tabIndex = 0, ariaHidden = false, topLeftBadgeProps, topRightBadgeProps, bottomLeftBadgeProps, bottomRightBadgeProps, withoutBorder = false, customSize = null, customBackgroundColor = null, onClick }) => {
    const overrideSquare = (0, backwardCompatibilityForProperties_1.backwardCompatibilityForProperties)([square, isSquare]);
    const overrideDisabled = (0, backwardCompatibilityForProperties_1.backwardCompatibilityForProperties)([disabled, isDisabled], false);
    const backgroundColorStyle = (0, react_1.useMemo)(() => {
        if (customBackgroundColor)
            return { backgroundColor: customBackgroundColor };
        return src ? {} : { backgroundColor: (0, colors_vars_map_1.getElementColor)(backgroundColor) };
    }, [src, backgroundColor, customBackgroundColor]);
    const sizeStyle = (0, react_1.useMemo)(() => {
        return customSize ? { height: customSize, width: customSize } : {};
    }, [customSize]);
    const overrideTooltipProps = (0, react_1.useMemo)(() => {
        if (withoutTooltip)
            return undefined;
        if (tooltipProps) {
            return Object.assign({ content: ariaLabel }, tooltipProps);
        }
        else {
            return { content: ariaLabel };
        }
    }, [ariaLabel, tooltipProps, withoutTooltip]);
    const badgesContainer = (0, react_1.useMemo)(() => {
        const badges = [];
        if (!(0, isNil_1.default)(topLeftBadgeProps)) {
            badges.push((0, jsx_runtime_1.jsx)("div", Object.assign({ className: (0, classnames_1.default)(bemHelper({ element: "badge" }), bemHelper({ element: "badge", state: "top-left" })) }, { children: (0, jsx_runtime_1.jsx)(AvatarBadge_1.AvatarBadge, Object.assign({ size: size }, topLeftBadgeProps)) }), "top-left-badge"));
        }
        if (!(0, isNil_1.default)(topRightBadgeProps)) {
            badges.push((0, jsx_runtime_1.jsx)("div", Object.assign({ className: (0, classnames_1.default)(bemHelper({ element: "badge" }), bemHelper({ element: "badge", state: "top-right" })) }, { children: (0, jsx_runtime_1.jsx)(AvatarBadge_1.AvatarBadge, Object.assign({ size: size }, topRightBadgeProps)) }), "top-right-badge"));
        }
        if (!(0, isNil_1.default)(bottomLeftBadgeProps)) {
            badges.push((0, jsx_runtime_1.jsx)("div", Object.assign({ className: (0, classnames_1.default)(bemHelper({ element: "badge" }), bemHelper({ element: "badge", state: "bottom-left" })) }, { children: (0, jsx_runtime_1.jsx)(AvatarBadge_1.AvatarBadge, Object.assign({ size: size }, bottomLeftBadgeProps)) }), "bottom-left-badge"));
        }
        if (!(0, isNil_1.default)(bottomRightBadgeProps)) {
            badges.push((0, jsx_runtime_1.jsx)("div", Object.assign({ className: (0, classnames_1.default)(bemHelper({ element: "badge" }), bemHelper({ element: "badge", state: "bottom-right" })) }, { children: (0, jsx_runtime_1.jsx)(AvatarBadge_1.AvatarBadge, Object.assign({ size: size }, bottomRightBadgeProps)) }), "bottom-right-bade"));
        }
        return badges.length > 0 ? (0, jsx_runtime_1.jsx)("div", Object.assign({ className: (0, classnames_1.default)(bemHelper({ element: "badges" })) }, { children: badges })) : null;
    }, [size, topLeftBadgeProps, topRightBadgeProps, bottomLeftBadgeProps, bottomRightBadgeProps]);
    const clickHandler = (0, react_1.useCallback)((event) => {
        event.preventDefault();
        if (onClick) {
            onClick(event);
        }
    }, [onClick]);
    return ((0, jsx_runtime_1.jsx)("div", Object.assign({ id: id, className: (0, classnames_1.default)(AVATAR_CSS_BASE_CLASS, className, bemHelper({ state: size })), style: sizeStyle }, { children: (0, jsx_runtime_1.jsx)(ClickableWrapper_1.default, Object.assign({ isClickable: !!onClick, clickableProps: {
                onClick: clickHandler,
                tabIndex: "-1",
                className: bemHelper({ element: "clickableWrapper" })
            } }, { children: (0, jsx_runtime_1.jsxs)(Tooltip_1.default, Object.assign({ showTrigger: [Dialog_1.default.hideShowTriggers.FOCUS, Dialog_1.default.hideShowTriggers.MOUSE_ENTER], hideTrigger: [Dialog_1.default.hideShowTriggers.BLUR, Dialog_1.default.hideShowTriggers.MOUSE_LEAVE] }, overrideTooltipProps, { children: [(0, jsx_runtime_1.jsx)("div", Object.assign({ className: (0, classnames_1.default)(bemHelper({ element: "circle" }), bemHelper({ element: "circle", state: type }), {
                            [bemHelper({ element: "circle", state: "is-disabled" })]: overrideDisabled,
                            [bemHelper({ element: "circle", state: "is-square" })]: overrideSquare,
                            [bemHelper({ element: "circle", state: "without-border" })]: withoutBorder
                        }), "aria-hidden": ariaHidden, tabIndex: tabIndex, style: Object.assign({}, backgroundColorStyle) }, { children: (0, jsx_runtime_1.jsx)(AvatarContent_1.AvatarContent, { type: type, size: size, src: src, icon: icon, text: text, ariaLabel: ariaLabel, role: role, textClassName: textClassName }) })), badgesContainer] })) })) })));
};
Object.assign(Avatar, {
    types: AvatarConstants_1.AvatarType,
    sizes: AvatarConstants_1.AvatarSize,
    colors: colors_vars_map_1.elementColorsNames,
    backgroundColors: colors_vars_map_1.elementColorsNames
});
exports.default = Avatar;
